import React, { useCallback, useEffect, useReducer } from 'react';

import { useSelector } from 'react-redux';
import { useSelectedView } from 'reducers/viewsSlice';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';

import { ANALYTICS_FILTERS_KEYS } from 'constants/ProjectConstants';
import { setFiltersToLS } from 'constants/helper';
import { localStorageKeys } from 'constants/localStorageKeys';

import { getLevelUpTargetDatesAndProgressStatuses } from 'services/growth/oneOnOne';

import { defaultLevelUpAnalyticsContextState, LevelUpAnalyticsContext } from '.';
import {
  levelUpAnalyticsCohortCreationActions,
  levelUpAnalyticsConstantsActions,
  levelUpAnalyticsFilterActions,
  levelUpAnalyticsOtherActions,
} from './actions';
import { levelUpAnalyticsCombinedReducer } from './reducers';

export const LevelUpAnalyticsProvider = ({ children }) => {
  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId);

  const [state, levelUpAnalyticsDispatch] = useReducer(
    levelUpAnalyticsCombinedReducer,
    defaultLevelUpAnalyticsContextState
  );

  const sideNavSelectedCohort = useSelectedView();

  const updateDateRange = useCallback(
    ({ startDate, endDate }) => {
      setFiltersToLS(localStorageKeys.analyticsFeedbackFilters, ANALYTICS_FILTERS_KEYS.dateRange, {
        startTS: startDate.getTime(),
        endTS: endDate.getTime(),
      });
      levelUpAnalyticsDispatch({
        type: levelUpAnalyticsFilterActions.SELECT_DATERANGE,
        payload: {
          dateRange: {
            startTS: startDate.getTime(),
            endTS: endDate.getTime(),
          },
        },
      });
    },
    [sideNavSelectedCohort]
  );

  const selectCohort = useCallback((cohort) => {
    // todo: to be removed as this is not used.
    return;

    // setFiltersToLS(
    //   localStorageKeys.analyticsLevelUpFilters,
    //   ANALYTICS_FILTERS_KEYS.cohortId,
    //   cohort.id
    // );
    levelUpAnalyticsDispatch({
      type: levelUpAnalyticsFilterActions.SELECT_COHORT,
      payload: { cohort },
    });
  }, []);

  const selectGroupBy = useCallback((groupByKey) => {
    // setFiltersToLS(
    //   localStorageKeys.analyticsLevelUpFilters,
    //   ANALYTICS_FILTERS_KEYS.groupByKey,
    //   groupByKey.keyName
    // );
    levelUpAnalyticsDispatch({
      type: levelUpAnalyticsFilterActions.SELECT_GROUPBY,
      payload: { groupByKey },
    });
  }, []);

  const selectSplitByKey = useCallback((splitBy) => {
    levelUpAnalyticsDispatch({
      type: levelUpAnalyticsFilterActions.SELECT_SPLIT_BY_KEY,
      payload: { splitBy },
    });
  }, []);

  const initializeState = useCallback((initialValues) => {
    levelUpAnalyticsDispatch({
      type: levelUpAnalyticsOtherActions.INITIALIZE_STATE,
      payload: initialValues,
    });
  }, []);

  const setOperationInProgress = useCallback((inProgress) => {
    levelUpAnalyticsDispatch({
      type: levelUpAnalyticsFilterActions.SET_OPERATION_IN_PROGRESS,
      payload: { inProgress },
    });
  }, []);

  // cohort creation functions.
  const addCohort = useCallback((cohort) => {
    setFiltersToLS(
      localStorageKeys.analyticsLevelUpFilters,
      ANALYTICS_FILTERS_KEYS.cohortId
      // cohort.uuid
    );
    levelUpAnalyticsDispatch({
      type: levelUpAnalyticsCohortCreationActions.ADD_COHORT,
      payload: { cohort },
    });
  }, []);

  // view change functions.
  const changeSelectedView = (viewId) => {
    levelUpAnalyticsDispatch({
      type: levelUpAnalyticsOtherActions.CHANGE_VIEW,
      payload: { viewId },
    });
  };

  const setExpandedRowDetailsView = ({ headerComponent, infoObj }) => {
    levelUpAnalyticsDispatch({
      type: levelUpAnalyticsOtherActions.SET_EXPANSION_VIEW_DETAILS,
      payload: {
        headerComponent,
        infoObj,
      },
    });
  };

  const clearExpandedRowDetailsView = () => {
    levelUpAnalyticsDispatch({
      type: levelUpAnalyticsOtherActions.CLEAR_EXPANSION_VIEW_DETAILS,
    });
  };

  const setNoData = (noData) => {
    levelUpAnalyticsDispatch({
      type: levelUpAnalyticsOtherActions.SET_NO_DATA,
      payload: {
        noData,
      },
    });
  };

  const setErrorData = (errorData) => {
    levelUpAnalyticsDispatch({
      type: levelUpAnalyticsOtherActions.SET_ERROR_DATA,
      payload: {
        errorData,
      },
    });
  };

  const updateTimeBoundPeriods = useCallback((timeBounds) => {
    setFiltersToLS(localStorageKeys.analyticsLevelUpFilters, ANALYTICS_FILTERS_KEYS.timeBounds, timeBounds);
    levelUpAnalyticsDispatch({
      type: levelUpAnalyticsFilterActions.SELECT_TIMEBOUND_PERIODS,
      payload: {
        timeBounds,
      },
    });
  }, []);

  const setLevelUpRiskEstimationList = useCallback((riskEstimationList) => {
    levelUpAnalyticsDispatch({
      type: levelUpAnalyticsConstantsActions.SET_LEVEL_UP_RISK_ESTIMATION_LIST,
      payload: riskEstimationList,
    });
  }, []);

  const setLevelUpProgressStatuses = useCallback((progressStatuses) => {
    levelUpAnalyticsDispatch({
      type: levelUpAnalyticsConstantsActions.SET_LEVEL_UP_PROGRESS_STATUSES,
      payload: progressStatuses,
    });
  }, []);

  useEffect(() => {
    // get Level up target dates and Progress Statuses, this is not employee specific
    getLevelUpTargetDatesAndProgressStatuses({ employeeId: loggedInEmployeeId })
      .then((res) => {
        const { statusList, riskEstimationList } = res;
        setLevelUpProgressStatuses(statusList);

        setLevelUpRiskEstimationList(riskEstimationList);
      })
      .catch((err) => console.error(err.message || 'failed to fetch level up details'));
  }, []);

  useEffect(() => {
    try {
      if (sideNavSelectedCohort) {
        if (sideNavSelectedCohort?.id === -2) {
          if (!state?.reactSelectGroupByForMyTeam?.find(({ value }) => state?.selectedGroupByKey?.value === value)) {
            selectGroupBy(state?.reactSelectGroupByForMyTeam[0]?.apiValue);
          }
        } else {
          const { allGroupByKeys } = state ?? {};

          const orgGroupByKey = allGroupByKeys?.find(({ value }) => value === 'orgTree');

          selectGroupBy(orgGroupByKey?.apiValue);
        }
      }
    } catch (error) {
      console.error('Error in changing groupBy of filter bar : ', error);
    }
  }, [sideNavSelectedCohort]);

  return (
    <LevelUpAnalyticsContext.Provider
      value={{
        state,
        updateDateRange,
        selectCohort,
        selectGroupBy,
        selectSplitByKey,
        initializeState,
        addCohort,
        changeSelectedView,
        setExpandedRowDetailsView,
        clearExpandedRowDetailsView,
        setNoData,
        setErrorData,
        updateTimeBoundPeriods,
        setOperationInProgress,
      }}
    >
      {children}
    </LevelUpAnalyticsContext.Provider>
  );
};
